// Titles for routes that failed to load or don't exist. The document title always comes from route
// heads, so the server sends it with the page; components never set it. `withStatusHeads` gives
// every route a head that, for an error or not-found match, replaces the page's title with the one
// ErrorPage shows (the router renders the failing match last, so its title wins).
import type { AnyRoute } from "@tanstack/react-router";
import { isRequestError } from "api-def";
import { type HeadMeta, resolveTitle } from "src/lib/Metadata";

export type ErrorType = "network-error" | "not-found" | "unauthorized" | "unknown";

/** What ErrorPage shows for an error: request errors with 401/403/404 are expected states. */
export const getErrorType = (error: unknown, type?: ErrorType): ErrorType => {
  if (type && type !== "unknown") return type;
  if (isRequestError(error)) {
    const status = error.response?.status;
    if (status === 401 || status === 403) return "unauthorized";
    if (status === 404) return "not-found";
  }
  return "unknown";
};

/** Document titles for ErrorPage's states (its headings are the translated versions). */
export const ERROR_PAGE_TITLES: Record<ErrorType, string> = {
  "not-found": "Page not found",
  unauthorized: "You don't have access to this page",
  unknown: "Something went wrong",
  "network-error": "You're offline",
};

interface StatusMatch {
  status: string;
  error?: unknown;
  _notFound?: boolean;
}

/** The title for a match that failed or is the not-found boundary, or undefined for a loaded one. */
export const getStatusTitle = (match: StatusMatch): string | undefined => {
  if (match._notFound || match.status === "notFound") {
    return resolveTitle(ERROR_PAGE_TITLES["not-found"]);
  }
  if (match.status === "error") return resolveTitle(ERROR_PAGE_TITLES[getErrorType(match.error)]);
  return undefined;
};

type HeadFn = (context: { match: StatusMatch }) => unknown;
interface HeadResult {
  meta?: HeadMeta[];
  [key: string]: unknown;
}

const wrapped = new WeakSet<AnyRoute>();

/** Wraps every route's `head` (adding one where a route has none) with the status title, once. */
export const withStatusHeads = <Tree extends AnyRoute>(tree: Tree): Tree => {
  const visit = (route: AnyRoute) => {
    if (wrapped.has(route)) return;
    wrapped.add(route);
    const options = route.options as { head?: HeadFn };
    const head = options.head;
    options.head = async (context) => {
      const result = ((await head?.(context)) ?? {}) as HeadResult;
      const title = getStatusTitle(context.match);
      if (!title) return result;
      const meta = (result.meta ?? []).filter((tag) => !(tag && "title" in tag));
      return { ...result, meta: [...meta, { title }] };
    };
    const children = route.children;
    const list = Array.isArray(children)
      ? children
      : children && typeof children === "object"
        ? Object.values(children)
        : [];
    for (const child of list) visit(child as AnyRoute);
  };
  visit(tree);
  return tree;
};
